import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject, of } from 'rxjs';
import {
  AHmonitoringModeEnum,
  AHplantPhasesEnum,
  AHpsc100InvertersType,
  AHscanDevicesEnum,
  AzzurroHubDataType,
  isNotNullOrUndefined,
  ProductModelService,
  productProperty,
  System,
} from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { map, shareReplay, tap } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectedSystemAzzurroHubData, SystemsState } from '../../store';
import { ColorTypeEnum, ColumnConfig, ColumnTypeEnum, PaginationConfig, UiPromiseService } from 'ngx-zcs-components';

export interface DeviceTable {
  device: ProductModelService;
  connection?: 'connected' | 'disconnected';
  loggerId: string;
  pcsInverters?: AHpsc100InvertersType[];
}

const getLabels = () => ({
  startRescan: 'Start Rescan',
  rescanningMessage: 'Azzurro Hub is rescanning devices, please wait...',
  rescanDisclaimerTitle: 'Azzurro Hub Rescan',
  rescanDisclaimerText: 'This will rescan the Azzurro Hub and update the device list. Please ensure that the Azzurro Hub is connected and operational.',
  actions: 'Actions',
  activatedSensor: 'Actived Sensor',
  authorization: 'Authorization',
  availableConnextUpgrade: 'Firmware update available.',
  connected: 'Connected',
  connection: 'Connection',
  device: 'Device',
  devicesTitle: 'Azzurro Hub devices',
  deviceType: 'Device type',
  disconnected: 'Disconnected',
  fwUpdateDescriptionError: 'Oops, error, try again!',
  fwUpdateDescriptionSuccess:
    'The update process has started successfully. Please wait a few moments until the process is complete. In the meantime, the Connext device will disconnect and reconnect automatically.',
  fwVersion: 'Fw version',
  integratedSensor: 'Integrated Sensor',
  inverterMaster: 'Inverter Master',
  localeCode: 'en-EN',
  maxPlantInvPwr: 'Max plant inverter power',
  meter: 'Meter',
  monitoringMode: 'Monitoring mode',
  noPccMonitoring: 'No PCC monitoring',
  phase: 'Phase',
  plantPower: 'Plant power',
  position: 'Position',
  powerMagic: 'Power Magic',
  serialNumber: 'Serial number',
  singlePhase: 'Single phase',
  skip: 'Skip',
  threePhase: 'Three phase',
  timezone: 'Timezone',
  title: 'Device details',
  update: 'update',
  updated: 'No updates available',
  updateFirmware: 'Update Firmware',
  updateToTheLatestVersion: 'Update the firmware to the latest version.',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-azzurro-hub-details',
  templateUrl: './azzurro-hub-details.component.html',
  styleUrls: ['./azzurro-hub-details.component.scss'],
})
export class AzzurroHubDetailsComponent implements ICustomBlock, IReduxBlock, AfterViewInit {
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;
  @ViewChild('psc100ActionsTemplate', { static: true }) psc100ActionsTemplate!: TemplateRef<any>;
  @ViewChild('psc100ExpansionTemplate', { static: true }) psc100ExpansionTemplate!: TemplateRef<any>;
  @Output() onDeviceSelected = new EventEmitter<string>();

  readonly _externalProperties$ = new BehaviorSubject<
    productProperty[] | undefined
  >(undefined);
  @Input() set externalDevices(properties: productProperty[] | undefined) {
    this._externalProperties$.next(properties);
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
    this.updateColumnLabels();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly isUpdatingFw$ = new BehaviorSubject<boolean>(false);
  readonly _instanceId$: Observable<string> = new Observable<string>();

  columns: ColumnConfig[] = [];
  pcsInvertersColumns: ColumnConfig[] = [
    {
      columnDef: 'deviceId',
      header: this.labels.device,
      type: ColumnTypeEnum.Text,
      cell: (row: { sn?: string }) => row.sn ?? '',
    },
    {
      columnDef: 'connection',
      header: this.labels.connection,
      type: ColumnTypeEnum.Chip,
      cell: (row: { connection?: 'connected' | 'disconnected' }) =>
        row.connection === 'connected' ? this.labels.connected : this.labels.disconnected,
      chipColor: (row: { connection?: 'connected' | 'disconnected' }) =>
        row.connection === 'connected' ? ColorTypeEnum.Green : ColorTypeEnum.Red,
    },
    {
      columnDef: 'deviceType',
      header: this.labels.deviceType,
      type: ColumnTypeEnum.Text,
      cell: () => 'inverter',
    },
    {
      columnDef: 'actions',
      header: this.labels.actions,
      type: ColumnTypeEnum.Template,
      template: this.psc100ActionsTemplate,
    },
  ];
  readonly deviceRowExpansionByRow = (row?: DeviceTable | null) =>
    row?.device?.type?.name === 'PSC100' && row.pcsInverters?.length ? this.psc100ExpansionTemplate : null;
  readonly deviceRowKey = (row?: DeviceTable | null) => row?.device?.id ?? '';

  getPcsInverters$(row?: DeviceTable | null) {
    return of(
      (row?.pcsInverters ?? []).map((inv) => ({
        sn: inv.sn,
        connection: row?.connection ?? 'disconnected',
      }))
    );
  }

  readonly azzurroHubData$: Observable<Partial<AzzurroHubDataType>> = combineLatest([
    this._refresh$,
    this.store.select(selectedSystemAzzurroHubData),
  ]).pipe(
    map(([_, data]) => data),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly deviceId$: Observable<string> = this.azzurroHubData$.pipe(
    isNotNullOrUndefined(),
    map((data) => data.loggerId || ''),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly fwVersion$: Observable<string | undefined> = this.azzurroHubData$.pipe(
    map((data) => data.info?.fw.currVer),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isConnected$: Observable<boolean> = this.azzurroHubData$.pipe(
    isNotNullOrUndefined(),
    map((data) => data.info?.connection?.status === 'connected'),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  azzurroHubScanInProgress: boolean = false;

  readonly isRescanning$: Observable<boolean> = this.azzurroHubData$.pipe(
    isNotNullOrUndefined(),
    map(data => data.config?.scanDevices === AHscanDevicesEnum.inProgress),
    tap(isInProgress => {
      if (isInProgress) {
        if (this.azzurroHubScanInProgress) {
          return;
        }
        this.uiPromiseService.run(() => new Promise<void>(() => {}), {
          loading: true,
          loadingMessage: 'Scan devices',
        });
        this.azzurroHubScanInProgress = true;
      } else {
        this.azzurroHubScanInProgress = false;
        this.uiPromiseService.hideAll();
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly canRescan$: Observable<boolean> = combineLatest([
    this.isRescanning$,
    this.isConnected$,
  ]).pipe(
    map(([isRescanning, isConnected]) => !isRescanning && isConnected),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly devices$: Observable<DeviceTable[] | null> = this.azzurroHubData$.pipe(
    map((data) => {
      const devices: DeviceTable[] = [];
      const loggerId = data.loggerId || '';
      const pcsDevices = data.PSC100 ?? [];
      // Gli inverter sono tutti quelli rilevati dall'hub, ma mostriamo solo quelli non presenti sui PSC100 (direttamente collegati all'Azzurro Hub).
      const pcsInverterSerials = new Set(
        pcsDevices.flatMap((pcs) => pcs.inverters?.map((inv) => inv.sn) ?? [])
      );
      const inverters = (data.inverter ?? []).filter((inv) => !pcsInverterSerials.has(inv.sn));
      inverters.forEach((inv) => {
        const product = new ProductModelService(inv.sn);
        devices.push({
          device: product,
          connection: inv.connected ? 'connected' : 'disconnected',
          loggerId,
        });
      });

      data.evChargerCAN?.forEach((ev) => {
        const product = new ProductModelService(ev.sn);
        devices.push({
          device: product,
          connection: ev.connected ? 'connected' : 'disconnected',
          loggerId,
        });
      });

      data.evCharger485?.forEach((ev) => {
        const product = new ProductModelService(ev.sn);
        devices.push({
          device: product,
          connection: ev.connected ? 'connected' : 'disconnected',
          loggerId,
        });
      });

      data.pwrMagic?.forEach((pwr) => {
        const product = new ProductModelService(pwr.sn);
        devices.push({
          device: product,
          connection: pwr.connected ? 'connected' : 'disconnected',
          loggerId,
        });
      });

      data.heatPump?.forEach((hp) => {
        const product = new ProductModelService(hp.sn);
        devices.push({
          device: product,
          connection: hp.connected ? 'connected' : 'disconnected',
          loggerId,
        });
      });

      pcsDevices.forEach((pcs) => {
        const product = new ProductModelService(pcs.sn);
        devices.push({
          device: product,
          connection: pcs.connected ? 'connected' : 'disconnected',
          loggerId,
          pcsInverters: pcs.inverters ?? [],
        });
      });

      return devices ?? null;
    }),
  );

  readonly connectionProperties$: Observable<productProperty[]> =
    this.azzurroHubData$.pipe(
      map((data) => {
        if (!data?.info?.connection?.status) return [];

        const isConnected = data.info.connection.status === 'connected';

        return [
          {
            name: this.labels.connection,
            value: isConnected
              ? this.labels.connected
              : this.labels.disconnected,
            icon: isConnected ? 'wifi' : 'wifi_off',
            iconCssClasses: isConnected
              ? 'bg-green-400 text-white radius-4'
              : 'bg-red-400 text-white radius-4',
          },
        ];
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  readonly azzurroHubDataProperties$: Observable<productProperty[]> =
    combineLatest([this.azzurroHubData$, this._externalProperties$]).pipe(
      map(([data, externalProps]) => {
        if (!data) return [];

        const properties: productProperty[] = [];

        if (data.info?.fw?.currVer) {
          properties.push({
            name: this.labels.fwVersion,
            value: data.info?.fw?.currVer.toUpperCase(),
            icon: 'memory',
            iconCssClasses: 'text-grey-400',
          });
        }

        if(data.info?.fw?.hwVer) {
          properties.push({
            name: 'versione hardware',
            value: data.info?.fw?.hwVer.toUpperCase(),
            icon: 'devices',
            iconCssClasses: 'text-grey-400',
          });
        }

        if(data.config?.monitoringMode) {
          properties.push({
            name: this.labels.monitoringMode,
            value: data.config?.monitoringMode === AHmonitoringModeEnum.invMaster
              ? this.labels.inverterMaster
              : data.config?.monitoringMode === AHmonitoringModeEnum.intSensor
                ? this.labels.integratedSensor
                : data.config?.monitoringMode === AHmonitoringModeEnum.meter
                  ? this.labels.meter
                  : data.config?.monitoringMode === AHmonitoringModeEnum.pwrMagic
                    ? this.labels.powerMagic
                    : data.config?.monitoringMode === AHmonitoringModeEnum.none
                      ? this.labels.noPccMonitoring
                      : this.labels.activatedSensor,
            icon: 'electric_meter',
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.config?.plantPhases) {
          properties.push({
            name: this.labels.phase,
            value:
              data.config?.plantPhases === AHplantPhasesEnum.monoPhase
                ? this.labels.singlePhase
                : this.labels.threePhase,
            icon: 'power_input',
            iconCssClasses: 'text-grey-400',
          });
        }

        if(data.config?.maxContractualPwr) {
          properties.push({
            name: this.labels.plantPower,
            value: data.config?.maxContractualPwr + ' W',
            icon: 'solar_power',
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.config?.maxPlantInvPwr) {
          properties.push({
            name: this.labels.maxPlantInvPwr,
            value: data.config?.maxPlantInvPwr + ' W',
            icon: 'solar_power',
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.config?.IANA) {
          const iana = data.config.IANA;
          properties.push({
            name: this.labels.timezone,
            value: iana,
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.lan?.eth_online || data.lan?.wifi_online) {
          properties.push({
            name: this.labels.connection,
            value: data.lan?.eth_online
              ? 'Ethernet'
              : 'Wi-Fi',
            icon: data.lan?.eth_online ? 'lan' : 'wifi',
            iconCssClasses: 'text-grey-400',
          });
        }


        return externalProps ? [...properties, ...externalProps] : properties;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  paginationConfig: PaginationConfig = {
    pageSize: 5,
    pageSizeOptions: [5, 10, 20],
  };

  constructor(
    private readonly uiPromiseService: UiPromiseService,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
  ) {}

  ngAfterViewInit(): void {
    this.updateColumnLabels();
  }

  updateColumnLabels(): void {
    this.columns = [
      {
        columnDef: 'deviceId',
        header: this.labels.device,
        type: ColumnTypeEnum.Text,
        cell: (row: DeviceTable) => `${row.device.id}`,
      },
      {
        columnDef: 'connection',
        header: this.labels.connection,
        type: ColumnTypeEnum.Chip,
        cell: (row: DeviceTable) => row.connection === 'connected' ? this.labels.connected : this.labels.disconnected,
        chipColor: (row: DeviceTable) => row.connection === 'connected' ? ColorTypeEnum.Green : ColorTypeEnum.Red,
      },
      {
        columnDef: 'deviceType',
        header: this.labels.deviceType,
        type: ColumnTypeEnum.Text,
        cell: (row: DeviceTable) => row.device.type?.name || 'Inverter',
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.actionsTemplate,
      }
    ];
    this.pcsInvertersColumns = [
      {
        columnDef: 'deviceId',
        header: this.labels.device,
        type: ColumnTypeEnum.Text,
        cell: (row: { sn?: string }) => row.sn ?? '',
      },
      {
        columnDef: 'connection',
        header: this.labels.connection,
        type: ColumnTypeEnum.Chip,
        cell: (row: { connection?: 'connected' | 'disconnected' }) =>
          row.connection === 'connected' ? this.labels.connected : this.labels.disconnected,
        chipColor: (row: { connection?: 'connected' | 'disconnected' }) =>
          row.connection === 'connected' ? ColorTypeEnum.Green : ColorTypeEnum.Red,
      },
      {
        columnDef: 'deviceType',
        header: this.labels.deviceType,
        type: ColumnTypeEnum.Text,
        cell: () => 'inverter',
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.psc100ActionsTemplate,
      },
    ];
  }
}
